import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport, TSegmentExportState } from '@feathr/blackbox';
import { Pill } from '@feathr/components';

interface IExportStateChipProps {
  segmentExport: SegmentExport;
}

interface IExportStateDisplay {
  theme: string;
  indicatorVariant: 'solid' | 'outline';
}

/**
 * Per-state pill theme + indicator. The 5 BE states collapse to 4 display
 * labels: `pending` and `running` both render as "In progress". Labels are
 * translated at the render site (not here) so static key extraction can see
 * each literal `t()` call.
 */
export const exportStateDisplay: Record<TSegmentExportState, IExportStateDisplay> = {
  pending: { theme: 'yellow', indicatorVariant: 'outline' },
  running: { theme: 'yellow', indicatorVariant: 'outline' },
  ready: { theme: 'green', indicatorVariant: 'solid' },
  failed: { theme: 'red', indicatorVariant: 'solid' },
  stopped: { theme: 'red', indicatorVariant: 'solid' },
};

function getStateLabel(state: TSegmentExportState, t: TFunction): string {
  switch (state) {
    case 'pending':

    case 'running':
      return t('In progress');

    case 'ready':
      return t('Completed');

    case 'failed':
      return t('Erroring');

    case 'stopped':
      return t('Stopped');
  }
}

function ExportStateChip({ segmentExport }: Readonly<IExportStateChipProps>): JSX.Element {
  const { t } = useTranslation();
  const state = segmentExport.get('state');
  const display = exportStateDisplay[state];

  return (
    <Pill indicatorVariant={display.indicatorVariant} theme={display.theme} variant={'secondary'}>
      {getStateLabel(state, t)}
    </Pill>
  );
}

export default observer(ExportStateChip);
